interface KioskMessage {
  action: string
  payload?: Record<string, unknown>
}

interface KioskError {
  code: number
  error: string
}

export interface KioskPaymentResult {
  txId: string
  status: 'approved' | 'declined' | 'error'
  amount: number
  timestamp: string
}

export interface KioskPrintResult {
  status: 'printed'
}

declare global {
  interface Window {
    KioskBridge?: (message: KioskMessage, callback?: (json: string) => void) => void & { __isStub?: boolean }
  }
}

// Install development stub when KioskBridge is not provided by Kiosk Shell (CEF)
if (typeof window.KioskBridge !== 'function') {
  const stub = (message: KioskMessage, callback?: (json: string) => void) => {
    const { action, payload } = message
    console.info('[KioskBridge STUB]', action, payload)

    let response: unknown

    switch (action) {
      case 'payment.processPayment':
        response = {
          txId: `TXN-STUB-${Date.now()}`,
          status: 'approved',
          amount: Number((payload as Record<string, unknown>)?.amount ?? 0),
          timestamp: new Date().toISOString(),
        } satisfies KioskPaymentResult
        break
      case 'payment.cancel':
        response = { status: 'cancelled' }
        break
      case 'payment.getStatus':
        response = { txId: (payload as Record<string, unknown>)?.txId, status: 'approved' }
        break
      case 'printer.print':
        response = { status: 'printed' } satisfies KioskPrintResult
        break
      case 'app.getConfig':
        response = { version: '1.0.0', buildNumber: '1', devMode: true }
        break
      case 'app.reload':
        response = {}
        break
      default:
        response = { status: 'ok', stub: true }
    }

    const delay = action === 'payment.processPayment' ? 3000 : 800
    setTimeout(() => callback?.(JSON.stringify(response)), delay)
  }

  ;(stub as unknown as { __isStub: boolean }).__isStub = true
  window.KioskBridge = stub as Window['KioskBridge']
}

/**
 * True when running inside a native Kiosk Shell (not the dev browser stub).
 */
export const isKiosk: boolean = !((window.KioskBridge as unknown as { __isStub?: boolean })?.__isStub === true)

/**
 * Promise-based wrapper for window.KioskBridge.
 * Rejects with { code, error } on KioskBridge errors or bridge unavailability.
 */
export function kioskCall<T = unknown>(action: string, payload: Record<string, unknown> = {}): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    if (typeof window.KioskBridge !== 'function') {
      reject(new Error('KioskBridge недоступен'))
      return
    }

    window.KioskBridge({ action, payload }, (json) => {
      try {
        const data = JSON.parse(json) as KioskError | T
        if (data && typeof (data as KioskError).code === 'number') {
          reject(data as KioskError)
        } else {
          resolve(data as T)
        }
      } catch {
        try {
          resolve(json as T)
        } catch {
          reject({ code: -1, error: 'Ошибка разбора ответа KioskBridge' })
        }

        reject({ code: -1, error: 'Ошибка разбора ответа KioskBridge' })
      }
    })
  })
}
